import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  CircularProgress,
  TextField,
} from "@mui/material";
import { UploadOutlined } from "@ant-design/icons";
import axios from "axios";
import toast from "react-hot-toast";
import { URL as BASE_URL } from "../config";

const AddCustomerVideoreviews = ({ review, onClose, refresh }) => {
  const [reviewTitle, setReviewTitle] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (review) {
      setReviewTitle(review.reviewTitle || "");
      setPreview(review.reviewVideo || null);
      setVideoFile(null);
    }
  }, [review]);

  useEffect(() => {
    return () => {
      if (preview && preview.startsWith("blob:")) {
        window.URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("video/")) {
      toast.error("Only video files allowed!");
      return;
    }

    setVideoFile(file);
    setPreview(window.URL.createObjectURL(file));
  };

  const handleSubmit = async () => {
    if (!reviewTitle.trim()) {
      toast.error("Please enter review title!");
      return;
    }

    if (!videoFile && !review?.reviewVideo) {
      toast.error("Please select a video!");
      return;
    }

    const data = new FormData();
    data.append("reviewTitle", reviewTitle);

    if (videoFile) {
      data.append("reviewVideo", videoFile);
    }

    setLoading(true);

    try {
      if (review) {
        await axios.patch(
          `${BASE_URL}/customervideoreviews/${review.reviewId}`,
          data,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        toast.success("Review updated successfully!");
      } else {
        await axios.post(`${BASE_URL}/customervideoreviews`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Review added successfully!");
      }

      refresh?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      toast.error("Upload failed!");
    } finally {
      setLoading(false);
    }
  };

  const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      minHeight: "28px",
      fontSize: "14px",
      "& fieldset": { borderColor: "#c4c4c4" },
      "&:hover fieldset": { borderColor: "#184459ff" },
      "&.Mui-focused fieldset": {
        borderColor: "#c4c4c4",
        borderWidth: "1.5px",
      },
    },
    "& .MuiInputLabel-root": {
      fontSize: "18px",
      color: "#184459ff",
      fontWeight: 600,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#184459ff",
    },
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        bgcolor: "rgba(0,0,0,0.6)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1300,
        p: 2,
      }}
    >
      <Box
        sx={{
          width: { xs: "90%", sm: 400 },
          bgcolor: "#fff",
          borderRadius: 2,
          p: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ textAlign: "center", color: "#184459ff", mb: 2 }}
        >
          {review ? "Edit Customer Review" : "Add Customer Review"}
        </Typography>

        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Review Title"
            variant="outlined"
            size="small"
            value={reviewTitle}
            onChange={(e) => setReviewTitle(e.target.value)}
            sx={fieldStyle}
            InputLabelProps={{ shrink: true }}
          />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid #c4c4c4",
              bgcolor: "#f9f9f9",
            }}
          >
            <Button
              component="label"
              startIcon={<UploadOutlined />}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                py: 0.7,
                px: 1.5,
                fontSize: "0.85rem",
              }}
            >
              Select Video
              <input
                type="file"
                hidden
                accept="video/*"
                onChange={handleFileChange}
              />
            </Button>

            {preview && (
              <video
                src={
                  preview.startsWith("blob:")
                    ? preview
                    : `${BASE_URL}${preview}`
                }
                width="80"
                height="50"
                style={{ borderRadius: "8px" }}
                controls
              />
            )}
          </Box>

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#184459ff",
                "&:hover": { backgroundColor: "#184459ff" },
              }}
              onClick={handleSubmit}
              startIcon={loading && <CircularProgress size={18} />}
              disabled={loading}
            >
              {loading
                ? "Uploading..."
                : review
                ? "Update"
                : "Upload"}
            </Button>

            <Button
              variant="contained"
              sx={{
                backgroundColor: "#757575ff",
                "&:hover": { backgroundColor: "#757575ff" },
              }}
              onClick={onClose}
              disabled={loading}
            >
              Close
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default AddCustomerVideoreviews;